import { State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GridWrapper from 'src/components/GridWrapper';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import useDebounce from 'src/hooks/useDebounce';
import useWindowSize from 'src/hooks/useWindowSize';
import { IDisableStep } from 'src/pages/EnterReceipts';
import PoLineItemWizard from 'src/sections/enter_receipt/enter_receipt_wizard/PoLineItemWizard';
import { usePoListQuery } from 'src/services/po';
import { IPO } from 'src/types/PO';
import { SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { formatEnterReceipts } from 'src/utils/transformation';
import InvoiceGridToolbar from '../enter_invoice/InvoiceGridToolBar';

const PO_DATA_ITEM_KEY: string = 'poId';
const PO_DATE_FIELD = 'statusDate';
const dateArr = [PO_DATE_FIELD];

const initialPODataState: State = {
  skip: 0,
  take: 20,
  sort: [
    { field: 'poStatus', dir: 'desc' },
    { field: 'statusDate', dir: 'asc' },
  ],
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: "PO's",
  DataItemKey: PO_DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface PoGridPops {
  companyId: string;
  setSelectedReceiptPoRow?: (data: IPO) => void;
  setSelectedInvoicePoRow?: (data: IPO) => void;
  poNumber?: string;
  supplierSearch: string;
  setSupplierSearch: SetState<string>;
  lineItemSearch: string;
  setLineItemSearch: SetState<string>;
  columns: any[];
  setDisableStep?: SetState<IDisableStep>;
  setPoSearch?: SetState<{ value: string; disable: boolean }>;
  setTotal?: SetState<number>;
  isPoAssigned?: boolean;
  isInvoice: boolean;
  isDetailsPage: boolean;
  isOcrRedirect?: boolean;
  handlePoSelection?: () => void;
  gridKey?: string;
}

const PoGrid: React.FC<PoGridPops> = ({
  companyId,
  setSelectedReceiptPoRow,
  setSelectedInvoicePoRow,
  poNumber,
  supplierSearch,
  setSupplierSearch,
  lineItemSearch,
  setLineItemSearch,
  columns,
  setDisableStep,
  setPoSearch,
  setTotal,
  isPoAssigned,
  isInvoice,
  isDetailsPage,
  isOcrRedirect,
  handlePoSelection,
  gridKey,
}) => {
  // Po state
  const [lineItemModal, setLineItemModal] = useState<boolean>(false);
  const supplierValue = useDebounce<string>(supplierSearch, 500);
  const lineItemValue = useDebounce<string>(lineItemSearch, 500);
  const searchPo = useDebounce<string>(poNumber || '', 500);
  const [poLineItemsData, setPoLineItemsData] = useState<IPO>({} as IPO);
  const [PODataState, setPODataState] = useState<State>(
    isPoAssigned ? initialPODataState : { ...initialPODataState, take: 20 }
  );
  const [POListData, setPOListData] = useState<IPO[]>([]);

  const postBody = useMemo(() => {
    const data = { ...modifyRequest(PODataState, dateArr, columns) };
    if (searchPo) {
      const filterByPo = { field: 'poNum', value: searchPo, operator: 'contains' };
      data.filters.push(filterByPo);
    }
    if (supplierValue) {
      const filterBySupplier = {
        field: 'supplierName',
        value: supplierValue,
        operator: 'contains',
      };
      data.filters = [...data.filters, filterBySupplier];
    }
    if (isPoAssigned) {
      const filterByPo = { field: 'poStatus', value: 'Closed', operator: 'neq' };
      data.filters.push(filterByPo);
    }
    return data;
  }, [PODataState, columns, searchPo, supplierValue, isPoAssigned]);

  const {
    data: poGridData,
    isLoading,
    isFetching,
  } = usePoListQuery({
    companyId,
    postBody,
    itemNumber: lineItemValue,
  });
  const loading = isLoading || isFetching;

  useEffect(() => {
    if (poGridData?.items) {
      setPOListData(formatEnterReceipts(poGridData.items, PO_DATE_FIELD));
    }
  }, [poGridData?.items]);

  const totalRecords = poGridData?.totalRecords || 0;

  useEffect(() => {
    setTotal?.(totalRecords);
    setDisableStep?.((prev) => ({ ...prev, disableNext: loading }));
  }, [totalRecords, loading, setTotal, setDisableStep]);

  const onPODataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setPODataState(e.dataState);
  }, []);

  const selectedRow = useMemo(
    () => POListData.find((item) => item.selected) || ({} as IPO),
    [POListData]
  );

  const onSelectionPoChange = React.useCallback((event: GridSelectionChangeEvent) => {
    setPOListData((prev: IPO[]) => {
      const prevItems = [...prev];
      const index = event?.dataItem?.rowId;
      return prevItems?.map((el: IPO) => ({ ...el, selected: el?.rowId === index }));
    });
  }, []);

  // Update selectedPoRow separately from POListData
  useEffect(() => {
    if (isInvoice) {
      setSelectedInvoicePoRow?.(selectedRow);
    } else {
      setSelectedReceiptPoRow?.(selectedRow);
    }
  }, [isInvoice, selectedRow, setSelectedInvoicePoRow, setSelectedReceiptPoRow]);

  useEffect(() => {
    if (isOcrRedirect && POListData.length === 1 && handlePoSelection) {
      handlePoSelection();
    }
  }, [POListData.length, isOcrRedirect, handlePoSelection, setDisableStep]);

  // clear filter
  const handleClearFilter = useCallback(() => {
    setSupplierSearch('');
    setLineItemSearch('');
    setPoSearch?.({ value: '', disable: false });
    setPODataState((prev) => ({
      ...initialPODataState,
      skip: prev.skip,
      take: prev.take,
    }));
    if (isInvoice) {
      setSelectedInvoicePoRow?.({} as IPO);
    } else {
      setSelectedReceiptPoRow?.({} as IPO);
    }
  }, [
    isInvoice,
    setLineItemSearch,
    setPoSearch,
    setSelectedInvoicePoRow,
    setSelectedReceiptPoRow,
    setSupplierSearch,
  ]);

  const handleClearSelection = useCallback(() => {
    setPOListData((prev: IPO[]) => prev.map((item: IPO) => ({ ...item, selected: false })));
  }, []);

  const toggleLineItemModal = useCallback(() => {
    setLineItemModal((prev) => !prev);
  }, []);

  const handlePoSaveLineItem = useCallback(() => {
    const selectedPoId = poLineItemsData?.poId || '';
    setPOListData((prev: IPO[]) =>
      prev.map((item: IPO) => {
        if (item.poId === selectedPoId) {
          return { ...item, selected: true };
        }
        return { ...item, selected: false };
      })
    );

    if (isInvoice) {
      setSelectedInvoicePoRow?.(poLineItemsData);
    } else {
      setSelectedReceiptPoRow?.(poLineItemsData);
    }
    toggleLineItemModal();
  }, [
    isInvoice,
    poLineItemsData,
    setSelectedInvoicePoRow,
    setSelectedReceiptPoRow,
    toggleLineItemModal,
  ]);

  const cellClick = useCallback(
    (data: IPO) => {
      setPoLineItemsData(data);
      toggleLineItemModal();
    },
    [toggleLineItemModal]
  );

  const { height } = useWindowSize();
  const isClearFiltersDisabled = useMemo(
    () => !PODataState.filter?.filters.length,
    [PODataState.filter?.filters.length]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <InvoiceGridToolbar
        clearFilter={handleClearFilter}
        clearSelection={handleClearSelection}
        isClearFiltersDisabled={isClearFiltersDisabled}
      />
    ),
    [handleClearFilter, handleClearSelection, isClearFiltersDisabled]
  );

  return (
    <>
      <div>
        <p className="font-bold text-sm mt-2">Showing Matching PO's For "{poNumber}"</p>
        <Dialog width="80%" open={lineItemModal} onClose={toggleLineItemModal} title="">
          <PoLineItemWizard
            title={`Showing items for PO # ${poLineItemsData?.poNum || ''}`}
            poId={poLineItemsData?.poId}
            isInvoice={isInvoice}
          />
          <DialogActionsBar>
            <div className="flex justify-end gap-2">
              <Button onClick={handlePoSaveLineItem} color="info">
                Exit and Select Record
              </Button>
              <Button color="info" onClick={toggleLineItemModal}>
                Exit
              </Button>
            </div>
          </DialogActionsBar>
        </Dialog>
        <GridWrapper
          columns={columns}
          dataSource={POListData || []}
          dataState={PODataState}
          key={gridKey}
          height={
            isDetailsPage
              ? height >= 950
                ? `${height - 458}px`
                : height >= 860
                ? `${height - 406}px`
                : `${height - 386}px`
              : `${height - 186}px`
          }
          total={totalRecords}
          onDataStateChange={onPODataStateChange}
          onSelectionChange={onSelectionPoChange}
          gridToolbarChildren={gridToolbarChildren}
          cellClick={() => ({ cellClick })}
          loading={loading}
          {...defaultProps}
        />
      </div>
    </>
  );
};

export default PoGrid;
